import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { 
  FileText,
  Upload,
  Download,
  Plus,
  Eye,
  X,
  CheckCircle,
  Clock,
  PaperPlaneTilt,
  Hospital,
  Printer,
  Sparkle,
  Warning,
  Heart,
  Pill,
  Syringe,
  Flask,
  Heartbeat
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const recordTypes = [
  { value: 'all', label: 'All Medical Records' },
  { value: 'medications', label: 'Medications' },
  { value: 'lab_results', label: 'Lab Results' },
  { value: 'imaging', label: 'Imaging (X-rays, MRI, CT)' },
  { value: 'visit_notes', label: 'Visit Notes' },
  { value: 'immunizations', label: 'Immunizations' },
  { value: 'surgical', label: 'Surgical Records' },
];

const purposes = [
  { value: 'continuing_care', label: 'Continuing Medical Care' },
  { value: 'personal', label: 'Personal Records' },
  { value: 'insurance', label: 'Insurance Purposes' },
  { value: 'legal', label: 'Legal Purposes' },
  { value: 'other', label: 'Other' },
];

const deliveryMethods = [
  { value: 'electronic', label: 'Electronic (Email/Portal)' },
  { value: 'fax', label: 'Fax' },
  { value: 'mail', label: 'Mail' },
  { value: 'pickup', label: 'In-Person Pickup' },
];

export default function HealthRecordsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('import'); // 'import' or 'request'
  const [loading, setLoading] = useState(false);
  
  // Import state
  const [importedRecords, setImportedRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Request state
  const [requests, setRequests] = useState([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({
    facility_name: '',
    facility_address: '',
    facility_phone: '',
    facility_fax: '',
    record_types: [],
    date_range_start: '',
    date_range_end: '',
    purpose: 'continuing_care',
    delivery_method: 'electronic',
    notes: ''
  });
  
  // PDF preview
  const [pdfHtml, setPdfHtml] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [recordsRes, requestsRes] = await Promise.all([
        api.get('/health-records/imported'),
        api.get('/health-records/requests')
      ]);
      setImportedRecords(recordsRes.data.records || []);
      setRequests(requestsRes.data.requests || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validExtensions = ['.xml', '.ccd', '.ccda'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExt)) {
      toast.error('Please upload a CCD/CCDA XML file (.xml, .ccd, or .ccda)');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/health-records/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Health records imported successfully!');
      setSelectedRecord(response.data);
      loadData();
    } catch (error) {
      toast.error('Failed to import health records');
      process.env.NODE_ENV === "development" && console.error(error);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    
    if (!requestForm.facility_name || !requestForm.facility_address || !requestForm.facility_phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (requestForm.record_types.length === 0) {
      toast.error('Please select at least one record type');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/health-records/request', requestForm);
      toast.success('Records request created successfully!');
      setShowRequestForm(false);
      setRequestForm({
        facility_name: '',
        facility_address: '',
        facility_phone: '',
        facility_fax: '',
        record_types: [],
        date_range_start: '',
        date_range_end: '',
        purpose: 'continuing_care',
        delivery_method: 'electronic',
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create records request');
      process.env.NODE_ENV === "development" && console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordTypeToggle = (value) => {
    setRequestForm(prev => ({
      ...prev,
      record_types: prev.record_types.includes(value)
        ? prev.record_types.filter(t => t !== value)
        : [...prev.record_types, value]
    }));
  };

  const viewRequestPdf = async (requestId) => {
    try {
      const response = await api.get(`/health-records/request/${requestId}/pdf`);
      setPdfHtml(response.data.html);
    } catch (error) {
      toast.error('Failed to generate form');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'received': return 'bg-blue-100 text-blue-700';
      case 'sent': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading && importedRecords.length === 0 && requests.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="health-records-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Health Records</h1>
        <p className="text-gray-600 mt-1">Import records from other providers or request your medical records</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('import')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'import'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="import-tab"
        >
          <Upload className="w-4 h-4 inline mr-2" />
          Import Records
        </button>
        <button
          onClick={() => setActiveTab('request')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'request'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="request-tab"
        >
          <PaperPlaneTilt className="w-4 h-4 inline mr-2" />
          Request Records
        </button>
      </div>

      {/* Import Tab */}
      {activeTab === 'import' && (
        <div className="space-y-6">
          {/* Upload Section */}
          <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-200">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white rounded-lg shadow-sm">
                <Sparkle className="w-8 h-8 text-teal-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Import from MyChart / Epic</h3>
                <p className="text-gray-600 mt-1 text-sm">
                  Download your health records from MyChart as a CCD/CCDA file and upload here. 
                  We&apos;ll automatically extract your medications, allergies, conditions, and more.
                </p>
                
                <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-800 mb-2">How to export from MyChart:</h4>
                  <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                    <li>Log into your MyChart account</li>
                    <li>Go to &quot;Menu&quot; → &quot;My Record&quot; or &quot;Health Summary&quot;</li>
                    <li>Click &quot;Download My Data&quot; or &quot;Export&quot;</li>
                    <li>Select CCD or CCDA format</li>
                    <li>Upload the downloaded file below</li>
                  </ol>
                </div>
                
                <div className="mt-4">
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 cursor-pointer transition-colors">
                    <Upload className="w-5 h-5" />
                    {uploading ? 'Uploading...' : 'Upload CCD/CCDA File'}
                    <input
                      type="file"
                      accept=".xml,.ccd,.ccda"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploading}
                      data-testid="file-upload-input"
                    />
                  </label>
                  <span className="ml-3 text-sm text-gray-500">Accepts .xml, .ccd, .ccda files</span>
                </div>
              </div>
            </div>
          </div>

          {/* Selected/Latest Import Result */}
          {selectedRecord && selectedRecord.parsed_data && (
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  <CheckCircle className="w-5 h-5 inline mr-2 text-green-600" />
                  Import Results
                </h3>
                <button
                  onClick={() => setSelectedRecord(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <Pill className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                  <div className="text-2xl font-bold text-blue-700">{selectedRecord.summary?.medications_found || 0}</div>
                  <div className="text-xs text-blue-600">Medications</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <Warning className="w-6 h-6 mx-auto text-red-600 mb-1" />
                  <div className="text-2xl font-bold text-red-700">{selectedRecord.summary?.allergies_found || 0}</div>
                  <div className="text-xs text-red-600">Allergies</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <Heart className="w-6 h-6 mx-auto text-purple-600 mb-1" />
                  <div className="text-2xl font-bold text-purple-700">{selectedRecord.summary?.problems_found || 0}</div>
                  <div className="text-xs text-purple-600">Conditions</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <Syringe className="w-6 h-6 mx-auto text-green-600 mb-1" />
                  <div className="text-2xl font-bold text-green-700">{selectedRecord.summary?.immunizations_found || 0}</div>
                  <div className="text-xs text-green-600">Immunizations</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3 text-center">
                  <Flask className="w-6 h-6 mx-auto text-yellow-600 mb-1" />
                  <div className="text-2xl font-bold text-yellow-700">{selectedRecord.summary?.lab_results_found || 0}</div>
                  <div className="text-xs text-yellow-600">Lab Results</div>
                </div>
                <div className="bg-teal-50 rounded-lg p-3 text-center">
                  <Heartbeat className="w-6 h-6 mx-auto text-teal-600 mb-1" />
                  <div className="text-2xl font-bold text-teal-700">{selectedRecord.summary?.vitals_found || 0}</div>
                  <div className="text-xs text-teal-600">Vitals</div>
                </div>
              </div>
              
              {/* Detailed Data */}
              <div className="grid md:grid-cols-2 gap-4">
                {selectedRecord.parsed_data.medications?.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <Pill className="w-4 h-4 text-blue-600" /> Medications
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {selectedRecord.parsed_data.medications.slice(0, 5).map((med) => (
                        <li key={`med-${med.name}-${med.dosage || ''}`}>• {med.name} {med.dosage && `- ${med.dosage}`}</li>
                      ))}
                      {selectedRecord.parsed_data.medications.length > 5 && (
                        <li className="text-teal-600">+ {selectedRecord.parsed_data.medications.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
                
                {selectedRecord.parsed_data.allergies?.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <Warning className="w-4 h-4 text-red-600" /> Allergies
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {selectedRecord.parsed_data.allergies.map((allergy) => (
                        <li key={`allergy-${allergy.allergen}`}>• {allergy.allergen}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedRecord.parsed_data.problems?.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <Heart className="w-4 h-4 text-purple-600" /> Conditions
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {selectedRecord.parsed_data.problems.slice(0, 5).map((prob) => (
                        <li key={`problem-${prob.condition}`}>• {prob.condition}</li>
                      ))}
                      {selectedRecord.parsed_data.problems.length > 5 && (
                        <li className="text-teal-600">+ {selectedRecord.parsed_data.problems.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
                
                {selectedRecord.parsed_data.immunizations?.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <Syringe className="w-4 h-4 text-green-600" /> Immunizations
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {selectedRecord.parsed_data.immunizations.slice(0, 5).map((imm) => (
                        <li key={`imm-${imm.vaccine}-${imm.date || ''}`}>• {imm.vaccine} {imm.date && `(${imm.date})`}</li>
                      ))}
                      {selectedRecord.parsed_data.immunizations.length > 5 && (
                        <li className="text-teal-600">+ {selectedRecord.parsed_data.immunizations.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Previous Imports */}
          {importedRecords.length > 0 && (
            <div className="bg-white rounded-xl border shadow-sm">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-gray-900">Previously Imported Records</h3>
              </div>
              <div className="divide-y">
                {importedRecords.map((record) => (
                  <div key={record.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-teal-600" />
                      <div>
                        <div className="font-medium text-gray-900">{record.file_name}</div>
                        <div className="text-sm text-gray-500">
                          Imported {new Date(record.import_date).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedRecord({ parsed_data: record.parsed_data, summary: {
                        medications_found: record.parsed_data?.medications?.length || 0,
                        allergies_found: record.parsed_data?.allergies?.length || 0,
                        problems_found: record.parsed_data?.problems?.length || 0,
                        immunizations_found: record.parsed_data?.immunizations?.length || 0,
                        lab_results_found: record.parsed_data?.lab_results?.length || 0,
                        vitals_found: record.parsed_data?.vitals?.length || 0,
                      }})}
                      className="text-teal-600 hover:text-teal-700"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Request Tab */}
      {activeTab === 'request' && (
        <div className="space-y-6">
          {/* Create Request Button */}
          {!showRequestForm && (
            <button
              onClick={() => setShowRequestForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              data-testid="new-request-btn"
            >
              <Plus className="w-5 h-5" />
              Request Records from Another Provider
            </button>
          )}

          {/* Request Form */}
          {showRequestForm && (
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  <Hospital className="w-5 h-5 inline mr-2 text-teal-600" />
                  Request Medical Records
                </h3>
                <button
                  onClick={() => setShowRequestForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleRequestSubmit} className="space-y-6">
                {/* Facility Info */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Provider/Facility Information</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Facility Name *
                      </label>
                      <input
                        type="text"
                        value={requestForm.facility_name}
                        onChange={(e) => setRequestForm({...requestForm, facility_name: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="e.g., City General Hospital"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        value={requestForm.facility_phone}
                        onChange={(e) => setRequestForm({...requestForm, facility_phone: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="(555) 123-4567"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address *
                      </label>
                      <input
                        type="text"
                        value={requestForm.facility_address}
                        onChange={(e) => setRequestForm({...requestForm, facility_address: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="123 Medical Center Dr, City, State ZIP"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fax (optional)
                      </label>
                      <input
                        type="tel"
                        value={requestForm.facility_fax}
                        onChange={(e) => setRequestForm({...requestForm, facility_fax: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="(555) 123-4568"
                      />
                    </div>
                  </div>
                </div>

                {/* Record Types */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Records Requested *</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {recordTypes.map((type) => (
                      <label
                        key={type.value}
                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                          requestForm.record_types.includes(type.value)
                            ? 'bg-teal-50 border-teal-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={requestForm.record_types.includes(type.value)}
                          onChange={() => handleRecordTypeToggle(type.value)}
                          className="rounded text-teal-600 focus:ring-teal-500"
                        />
                        <span className="text-sm">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Date Range */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Date Range (optional)</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                      <input
                        type="date"
                        value={requestForm.date_range_start}
                        onChange={(e) => setRequestForm({...requestForm, date_range_start: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                      <input
                        type="date"
                        value={requestForm.date_range_end}
                        onChange={(e) => setRequestForm({...requestForm, date_range_end: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Purpose & Delivery */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
                    <select
                      value={requestForm.purpose}
                      onChange={(e) => setRequestForm({...requestForm, purpose: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      {purposes.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Method</label>
                    <select
                      value={requestForm.delivery_method}
                      onChange={(e) => setRequestForm({...requestForm, delivery_method: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      {deliveryMethods.map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                  <textarea
                    value={requestForm.notes}
                    onChange={(e) => setRequestForm({...requestForm, notes: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Any specific information or instructions..."
                  />
                </div>

                {/* Submit */}
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
                  >
                    {loading ? 'Creating...' : 'Create Request'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRequestForm(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Existing Requests */}
          {requests.length > 0 && (
            <div className="bg-white rounded-xl border shadow-sm">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-gray-900">Your Records Requests</h3>
              </div>
              <div className="divide-y">
                {requests.map((req) => (
                  <div key={req.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{req.facility_name}</div>
                        <div className="text-sm text-gray-500">{req.facility_address}</div>
                        <div className="text-sm text-gray-500 mt-1">
                          Records: {req.record_types.join(', ')}
                        </div>
                        <div className="mt-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(req.status)}`}>
                            {req.status === 'pending' && <Clock className="w-3 h-3" />}
                            {req.status === 'completed' && <CheckCircle className="w-3 h-3" />}
                            {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => viewRequestPdf(req.id)}
                          className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                          title="View/Print Form"
                        >
                          <Printer className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {requests.length === 0 && !showRequestForm && (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <FileText className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <h3 className="text-lg font-medium text-gray-900">No Records Requests Yet</h3>
              <p className="text-gray-500 mt-1">Click the button above to request your records from another provider</p>
            </div>
          )}
        </div>
      )}

      {/* PDF Preview Modal */}
      {pdfHtml && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Medical Records Request Form</h3>
              <button
                onClick={() => setPdfHtml(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe
                srcDoc={pdfHtml}
                className="w-full h-full min-h-[600px]"
                title="Records Request Form"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
